import { useRef, useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Button, Paper, Box, Typography } from '@mui/material';
import styled from "styled-components";
import { Download, Description } from '@mui/icons-material';
import * as XLSX from 'xlsx-js-style';
import axios from 'axios';
import { getStoredCompanyDetails, getCompanyDisplayFromDetails, getShortCompanyName } from '../../services/companyService';

const BankStatementGenerated = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const tableRef = useRef(null);
    const [forceUpdate, setForceUpdate] = useState(0);

    // Get data from location state (with fallback for hooks)
    const reportData = location.state?.reportData || null;
    const month = location.state?.month || '';
    const year = location.state?.year || '';
    const creditNarration = location.state?.creditNarration || '';
    const debitNarration = location.state?.debitNarration || '';

    const companyDetails = useSelector((state) => state.auth.activeCompanyDetails) || getStoredCompanyDetails();
    const { companyName: globalCompanyName, addressLine1, addressLine2 } = getCompanyDisplayFromDetails(companyDetails || {});
    const company_name = reportData?.company_name || globalCompanyName || '';
    const bank_account_number = reportData?.bank_account_number || null;
    const internet_banking_id = reportData?.internet_banking_id || null;
    const employees = reportData?.employees || [];

    // Check if IFSC data is present
    const hasIfscData = employees.length > 0 && employees[0].ifsc_code !== undefined;

    // Calculate total net pay
    const totalNetPay = employees.reduce((sum, emp) => sum + emp.net_pay, 0);

    // Redirect if no data (after all hooks)
    useEffect(() => {
        if (!location.state || !location.state.reportData) {
            navigate('/reports/bank-statement-report');
        }
    }, [location.state, navigate]);

    // Helper function to clean bank account numbers for display (removes all quotes)
    const cleanDisplayValue = (value) => {
        if (value === null || value === undefined || value === '') return '';
        let cleaned = String(value);
        cleaned = cleaned.replace(/'/g, '');
        cleaned = cleaned.replace(/[''""]/g, '');
        cleaned = cleaned.trim();
        return cleaned;
    };

    // Effect to ensure bank account cells are always clean (no quotes) in the UI
    useEffect(() => {
        const cleanCells = () => {
            if (!tableRef.current) return;
            const bankAccountCells = tableRef.current.querySelectorAll('td[data-bank-account="true"]');
            bankAccountCells.forEach((cell) => {
                let cellText = cell.textContent || cell.innerText || '';
                const cleanValue = cellText.replace(/'/g, '').replace(/[''""]/g, '').trim();
                if (cellText !== cleanValue) {
                    cell.textContent = cleanValue;
                    cell.innerHTML = cleanValue;
                }
            });
        };
        
        cleanCells();
        const timer1 = setTimeout(cleanCells, 50);
        const timer2 = setTimeout(cleanCells, 100);
        const timer3 = setTimeout(cleanCells, 300);
        const timer4 = setTimeout(cleanCells, 500);
        return () => {
            clearTimeout(timer1);
            clearTimeout(timer2);
            clearTimeout(timer3);
            clearTimeout(timer4);
        };
    }, [employees, forceUpdate]);

    // Helper function to get first 4 digits of account number
    const getFirst4Digits = (accountNumber) => {
        if (!accountNumber) return '0000';
        const cleanedAccount = cleanDisplayValue(accountNumber);
        return cleanedAccount.substring(0, 4).padEnd(4, '0');
    };

    // Helper function to get ordinal suffix (1st, 2nd, 3rd, etc.)
    const getOrdinalSuffix = (num) => {
        if (num === 1) return '1st';
        if (num === 2) return '2nd';
        if (num === 3) return '3rd';
        return `${num}th`;
    };

    const getUploadCounterStorageKey = (monthValue, yearValue) => {
        const monthAbbr = monthValue.substring(0, 3);
        const yearShort = String(yearValue).slice(-2);
        return `bank_statement_counter_${monthAbbr}${yearShort}`;
    };

    const consumeUploadCounter = (monthValue, yearValue) => {
        const storageKey = getUploadCounterStorageKey(monthValue, yearValue);
        const counter = parseInt(localStorage.getItem(storageKey) || '1', 10);
        localStorage.setItem(storageKey, (counter + 1).toString());
        return counter;
    };

    const getIciciSalaryUploadExcelFilename = (companyNameValue, monthValue, yearValue, uploadCounter) => {
        const reportCompanyName = getShortCompanyName(companyNameValue) || 'Report';
        return `${reportCompanyName} ${getOrdinalSuffix(uploadCounter)} salary upload for ICICI ${monthValue.substring(0, 3)} ${yearValue}.xlsx`;
    };

    // Download as Notepad (Text File) in specified format
    const handleNotepadDownload = async () => {
        const uploadCounter = consumeUploadCounter(month, year);

        // Download ICICI summary Excel together with text file
        await handleIciciExcelDownload(uploadCounter);

        let textContent = '';
        
        // Internet banking ID line (use "-" if not available)
        textContent += `${internet_banking_id || '-'}\n`;

        // Helper: zero-pad account to 12 digits
        const formatAccount12 = (accountNumber) => {
            if (!accountNumber) return '000000000000';
            const cleaned = cleanDisplayValue(accountNumber);
            return cleaned.padStart(12, '0').substring(0, 12);
        };

        // Helper: right-align amount in a 15-char field
        const formatAmount15 = (amount) => {
            const amtStr = String(amount);
            return amtStr.padStart(15, ' ');
        };

        const totalNetPayRounded = employees.reduce((sum, emp) => sum + Math.round(emp.net_pay || 0), 0);

        // First line: Debit entry (total amount)
        const first4Digits = bank_account_number ? getFirst4Digits(bank_account_number) : '0000';
        const formattedBankAccount = formatAccount12(bank_account_number);
        const debitInrCode = `INR${first4Digits}`;
        const debitAmount = totalNetPayRounded.toFixed(2);

        // Debit line: [12-digit account][4 spaces][INR+4digits][4 spaces][D][15-char right-aligned amount][narration]
        textContent += `${formattedBankAccount}    ${debitInrCode}    D${formatAmount15(debitAmount)}${debitNarration}\n`;

        // Credit entries for each employee - same column structure
        employees.forEach((emp) => {
            const empFirst4 = getFirst4Digits(emp.account_number);
            const empAccount = formatAccount12(emp.account_number);
            const creditInrCode = `INR${empFirst4}`;
            const creditAmount = Math.round(emp.net_pay || 0).toFixed(2);

            // Credit line: [12-digit account][4 spaces][INR+4digits][4 spaces][C][15-char right-aligned amount][narration]
            textContent += `${empAccount}    ${creditInrCode}    C${formatAmount15(creditAmount)}${creditNarration}\n`;
        });
        
        // Generate dynamic filename with counter
        const monthAbbr = month.substring(0, 3); // "November" -> "Nov"
        const yearShort = String(year).slice(-2); // "2025" -> "25"
        const filename = `${getOrdinalSuffix(uploadCounter)}upld${monthAbbr}${yearShort}Sal.txt`;
        
        // Create blob and download
        const blob = new Blob([textContent], { type: 'text/plain' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    };

    const buildEmployeePanMap = (masterEmployees) => {
        const byId = new Map();
        const byCode = new Map();
        (masterEmployees || []).forEach((emp) => {
            const pan = String(emp?.pan_card_no || '').trim();
            if (emp?.employee_id != null) byId.set(emp.employee_id, pan);
            const code = String(emp?.employee_code || '').trim();
            if (code) byCode.set(code, pan);
        });
        return { byId, byCode };
    };

    const getPanForEmployee = (emp, panMap) => {
        const panById = panMap.byId.get(emp?.employee_id);
        if (panById) return panById;
        const code = String(emp?.employee_code || emp?.employeeCode || '').trim();
        return panMap.byCode.get(code) || '';
    };

    // ICICI simple Excel (Sr No, Employee Code, PAN, Name, Net Pay)
    const handleIciciExcelDownload = async (uploadCounter) => {
        let panMap = { byId: new Map(), byCode: new Map() };
        try {
            const masterResponse = await axios.get(
                `${process.env.REACT_APP_API_BASE_URL}/employee-master/view-employees`
            );
            panMap = buildEmployeePanMap(masterResponse.data);
        } catch (error) {
            console.error('Failed to fetch employee PAN details:', error);
        }

        const wb = XLSX.utils.book_new();
        let totalNetPay = 0;

        const wsData = [
            ['Sr No', 'Employee Code', 'PAN Number', 'Employee Name', 'Net Pay']
        ];

        employees.forEach((emp, index) => {
            const name = (emp?.full_name && String(emp.full_name).trim())
                || `${emp?.first_name || ''} ${emp?.last_name || ''}`.trim();
            const netPay = Math.round(emp?.net_pay || 0);
            totalNetPay += netPay;

            wsData.push([
                index + 1,
                emp?.employee_code || emp?.employeeCode || '',
                getPanForEmployee(emp, panMap),
                name,
                netPay
            ]);
        });

        wsData.push(['', '', '', 'Grand Total', totalNetPay]);

        const ws = XLSX.utils.aoa_to_sheet(wsData);

        // Basic column widths for readability
        ws['!cols'] = [
            { wch: 8 },
            { wch: 16 },
            { wch: 14 },
            { wch: 28 },
            { wch: 14 }
        ];

        const grandTotalRowIndex = employees.length + 1;
        // Number format for Net Pay column (E) and bold grand total row
        for (let r = 1; r < wsData.length; r++) {
            const cell = ws[XLSX.utils.encode_cell({ r, c: 4 })];
            if (cell) cell.z = '#,##0';
            if (r === grandTotalRowIndex) {
                for (let c = 0; c < 5; c++) {
                    const totalCell = ws[XLSX.utils.encode_cell({ r, c })];
                    if (totalCell) {
                        totalCell.s = { font: { bold: true } };
                    }
                }
            }
        }

        XLSX.utils.book_append_sheet(wb, ws, 'ICIC');

        const counter = uploadCounter ?? consumeUploadCounter(month, year);
        const filename = getIciciSalaryUploadExcelFilename(company_name, month, year, counter);
        XLSX.writeFile(wb, filename);
    };

    // Get current date in format YYYYMMDD
    const getCurrentDateString = () => {
        const now = new Date();
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const day = String(now.getDate()).padStart(2, '0');
        return `${year}${month}${day}`;
    };

    // Get current date in format dd-mm-yyyy
    const getFormattedDate = () => {
        const now = new Date();
        const day = String(now.getDate()).padStart(2, '0');
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const year = now.getFullYear();
        return `${day}-${month}-${year}`;
    };

    // Download Excel file with IFSC data
    const handleExcelDownload = () => {
        // Prepare data for Excel
        const excelData = [];
        const numCols = 19;
        
        // // Company header rows from company details table
        // const companyRow1 = [company_name || ''];
        // for (let i = 1; i < numCols; i++) companyRow1.push('');
        // excelData.push(companyRow1);

        // const companyRow2 = [addressLine1 || ''];
        // for (let i = 1; i < numCols; i++) companyRow2.push('');
        // excelData.push(companyRow2);

        // const companyRow3 = [addressLine2 || ''];
        // for (let i = 1; i < numCols; i++) companyRow3.push('');
        // excelData.push(companyRow3);

        // const titleRow = [`Bank Statement details for ${month} ${year}`];
        // for (let i = 1; i < numCols; i++) titleRow.push('');
        // excelData.push(titleRow);
        
        // Add column header row
        excelData.push([
            'PYMT_PROD_TYPE_CODE',
            'PYMT_MODE',
            'DEBIT_ACC_NO',
            'BNF_NAME',
            'BENE_ACC_NO',
            'BENE_IFSC',
            'AMOUNT',
            'DEBIT_NARR',
            'CREDIT_NARR',
            'MOBILE_NUM',
            'EMAIL_ID',
            'REMARK',
            'PYMT_DATE',
            'REF_NO',
            'ADDL_INFO1',
            'ADDL_INFO2',
            'ADDL_INFO3',
            'ADDL_INFO4',
            'LEI_NUMBER'
        ]);

        // Get formatted date for PYMT_DATE column
        const currentDate = getFormattedDate();

        // Add data rows
        employees.forEach((emp) => {
            const fullName = `${emp.first_name || ''} ${emp.last_name || ''}`.trim();
            excelData.push([
                'PAB_VENDOR',      // PYMT_PROD_TYPE_CODE
                'NEFT',            // PYMT_MODE
                bank_account_number || '', // DEBIT_ACC_NO
                fullName,          // BNF_NAME
                emp.account_number || '', // BENE_ACC_NO
                emp.ifsc_code || '', // BENE_IFSC
                emp.net_pay || 0,  // AMOUNT
                debitNarration,    // DEBIT_NARR
                creditNarration,   // CREDIT_NARR
                emp.mobile_no || '', // MOBILE_NUM
                emp.email || '',   // EMAIL_ID
                '',                // REMARK
                currentDate,       // PYMT_DATE (dd-mm-yyyy format)
                '',                // REF_NO
                '',                // ADDL_INFO1
                '',                // ADDL_INFO2
                '',                // ADDL_INFO3
                '',                // ADDL_INFO4
                ''                 // LEI_NUMBER
            ]);
        });

        // Create workbook and worksheet
        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.aoa_to_sheet(excelData);

        // Calculate column widths based on content
        const calculateColumnWidths = (data) => {
            const colWidths = [];
            const numCols = data[0]?.length || 19;
            
            for (let col = 0; col < numCols; col++) {
                let maxWidth = 0;
                
                data.forEach((row, rowIndex) => {
                    // Skip only the column header row
                    if (rowIndex < 1) return;
                    
                    if (row[col] !== undefined && row[col] !== null) {
                        const cellValue = String(row[col]);
                        maxWidth = Math.max(maxWidth, cellValue.length);
                    }
                });
                
                // Add small padding (1 character) for better readability
                colWidths.push({ wch: maxWidth + 1 });
            }
            
            return colWidths;
        };

        // Set column widths dynamically based on content
        ws['!cols'] = calculateColumnWidths(excelData);

        // Common border style
        const borderStyle = {
            top: { style: "thin", color: { rgb: "000000" } },
            bottom: { style: "thin", color: { rgb: "000000" } },
            left: { style: "thin", color: { rgb: "000000" } },
            right: { style: "thin", color: { rgb: "000000" } }
        };

    // Style the first 7 column headers and PYMT_DATE with red text color
    const redHeaderStyle = {
      font: { color: { rgb: "FF0000" }, bold: true },
      fill: { fgColor: { rgb: "5B9BD5" } },
      alignment: { horizontal: "center", vertical: "center" },
      border: borderStyle
    };

    // Style remaining column headers with lighter blue background
    const blueHeaderStyle = {
      font: { bold: true, color: { rgb: "FFFFFF" } },
      fill: { fgColor: { rgb: "5B9BD5" } },
      alignment: { horizontal: "center", vertical: "center" },
      border: borderStyle
    };

        // Apply red style to first 7 columns (A1-G1) and PYMT_DATE (M1)
        const redHeaderCells = ['A1', 'B1', 'C1', 'D1', 'E1', 'F1', 'G1', 'M1'];
        redHeaderCells.forEach(cell => {
            if (ws[cell]) {
                ws[cell].s = redHeaderStyle;
            }
        });

        // Apply blue style to remaining columns (H1-L1, N1-S1)
        const blueHeaderCells = ['H1', 'I1', 'J1', 'K1', 'L1', 'N1', 'O1', 'P1', 'Q1', 'R1', 'S1'];
        blueHeaderCells.forEach(cell => {
            if (ws[cell]) {
                ws[cell].s = blueHeaderStyle;
            }
        });

        // Apply data cell styles
        const range = XLSX.utils.decode_range(ws['!ref']);
        for (let row = 1; row <= range.e.r; row++) {
            for (let col = 0; col < 19; col++) {
                const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
                if (ws[cellAddress]) {
                    ws[cellAddress].s = {
                        border: borderStyle,
                        alignment: { vertical: "center", horizontal: "left" }
                    };
                    // AMOUNT column (G) with comma-separated format
                    if (col === 6) {
                        ws[cellAddress].z = '#,##0';
                    }
                }
            }
        }

        // Add worksheet to workbook
        XLSX.utils.book_append_sheet(wb, ws, 'Bank Statement');

        // Generate filename
        const filename = `${getShortCompanyName(company_name) || 'Report'} Report for Bank Statement ${month.substring(0, 3)} ${year}.xlsx`;

        // Download file
        XLSX.writeFile(wb, filename);
    };
    
    // Don't render if no data (will redirect via useEffect)
    if (!reportData || employees.length === 0) {
        return null;
    }

    return (
        <Box sx={{display: "flex", justifyContent: "center", alignItems: "flex-start", minHeight: "100vh", padding: "20px"}}>
            <Paper elevation={5} sx={{width: "1000px", margin: '0 auto', padding: '30px', position: 'relative'}}>
                {/* Back Button - Top Left */}
                <Box sx={{position: 'absolute', top: '20px', left: '20px'}}>
                    <Button
                        variant='outlined'
                        color='primary'
                        size="small"
                        onClick={() => navigate('/reports/bank-statement-report')}
                    >
                        ← Back
                    </Button>
                </Box>

                {/* Narration Section */}
                <Box sx={{marginBottom: '20px', padding: '15px', backgroundColor: '#f5f5f5', borderRadius: '5px'}}>
                    <Typography variant="body2" sx={{marginBottom: '8px'}}>
                        <strong>Credit Narration:</strong> {creditNarration}
                    </Typography>
                    <Typography variant="body2">
                        <strong>Debit Narration:</strong> {debitNarration}
                    </Typography>
                </Box>

                {/* Table */}
                <TABLE ref={tableRef}>
                    <tbody>
                        <tr>
                            <TH>Sr. No.</TH>
                            {hasIfscData && (
                                <>
                                    <TH>First Name</TH>
                                    <TH>Last Name</TH>
                                </>
                            )}
                            <TH>Account Number</TH>
                            {hasIfscData && <TH>IFSC Code</TH>}
                            {hasIfscData && <TH>Mobile Number</TH>}
                            {hasIfscData && <TH>Email</TH>}
                            <TH>Net Pay</TH>
                        </tr>
                        {employees.map((emp, index) => (
                            <tr key={emp.employee_id}>
                                <TD>{index + 1}</TD>
                                {hasIfscData && (
                                    <>
                                        <TD>{emp.first_name || ''}</TD>
                                        <TD>{emp.last_name || ''}</TD>
                                    </>
                                )}
                                <TD data-bank-account="true">{cleanDisplayValue(emp.account_number)}</TD>
                                {hasIfscData && <TD>{emp.ifsc_code || ''}</TD>}
                                {hasIfscData && <TD>{emp.mobile_no || ''}</TD>}
                                {hasIfscData && <TD>{emp.email || ''}</TD>}
                                <TD>₹{emp.net_pay.toLocaleString('en-IN')}</TD>
                            </tr>
                        ))}
                        <tr style={{backgroundColor: '#f0f0f0'}}>
                            <TD colSpan={hasIfscData ? "7" : "2"} style={{textAlign: 'center'}}><b>Total Net Pay:</b></TD>
                            <TD style={{textAlign: 'center'}}><b>₹{totalNetPay.toLocaleString('en-IN')}</b></TD>
                        </tr>
                    </tbody>
                </TABLE>

                {/* Download Buttons - Below Table */}
                <Box sx={{marginTop: '20px', textAlign: 'center', display: 'flex', gap: 2, justifyContent: 'center'}}>
                    {!hasIfscData && (
                        <Button
                            variant='contained'
                            color='success'
                            startIcon={<Download />}
                            onClick={handleNotepadDownload}
                        >
                            Download Text File
                        </Button>
                    )}
                    
                    {hasIfscData && (
                        <Button
                            variant='contained'
                            color='primary'
                            startIcon={<Description />}
                            onClick={handleExcelDownload}
                        >
                            Download Excel
                        </Button>
                    )}
                </Box>
            </Paper>
        </Box>
    );
}

export default BankStatementGenerated;

const TABLE = styled.table`
    width: 100%;
    border-collapse: collapse;
    border: 1px solid #ddd;
`;

const TH = styled.th`
    border-bottom: 2px solid #333;
    padding: 10px;
    background-color: #f5f5f5;
    font-weight: bold;
    text-align: center;
`;

const TD = styled.td`
    border-bottom: 1px solid #ddd;
    padding: 8px;
    text-align: center;
`;

